import type { DebugMeta } from './debugRecorder';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';

declare global {
  interface Window {
    __DICE_DEBUG__?: boolean;
  }
}

export function debugLog(label: string, payload: unknown, meta?: DebugMeta) {
  if (isVerboseLoggingEnabled()) {
    logger.debug(`DEBUG_${label}`, payload);
  }
  recordDebugTrace(label, payload, meta);
}

export type { DebugMeta };
export type DebugRecordOptions = {
  force?: boolean;
  flushReason?: string;
};

export type DebugRecorderContextPatch = {
  gameId?: string | null;
  playerId?: string | null;
  playerName?: string | null;
  isHost?: boolean | null;
  clientBuild?: string | null;
};

export type DebugDynamicContextProvider = () => Record<string, unknown> | undefined;

type DebugRecorderLike = {
  record: (label: string, payload: unknown, meta?: DebugMeta, options?: DebugRecordOptions) => void;
  setContext: (patch: DebugRecorderContextPatch) => void;
  setDynamicContextProvider: (provider?: DebugDynamicContextProvider) => void;
};

let debugRecorderPromise: Promise<DebugRecorderLike> | null = null;

function withDebugRecorder(callback: (recorder: DebugRecorderLike) => void): void {
  // PartyKit/server-side imports reducer helpers too; avoid eager debugRecorder import there.
  if (typeof window === 'undefined') return;
  void loadDebugRecorder()
    .then((recorder) => {
      callback(recorder);
    })
    .catch(() => {
      // Debug tracing must never break runtime flow.
    });
}

function loadDebugRecorder(): Promise<DebugRecorderLike> {
  if (!debugRecorderPromise) {
    debugRecorderPromise = import('./debugRecorder')
      .then((mod) => mod.debugRecorder as DebugRecorderLike);
  }
  return debugRecorderPromise;
}

export function recordDebugTrace(
  label: string,
  payload: unknown,
  meta?: DebugMeta,
  options?: DebugRecordOptions,
): void {
  withDebugRecorder((recorder) => {
    recorder.record(label, payload, meta, options);
  });
}

export function setDebugRecorderContext(context: DebugRecorderContextPatch): void {
  withDebugRecorder((recorder) => {
    recorder.setContext(context);
  });
}

export function setDebugDynamicContextProvider(provider?: DebugDynamicContextProvider): void {
  withDebugRecorder((recorder) => {
    recorder.setDynamicContextProvider(provider);
  });
}
